import {OpacityMaterial, EDLMaterial, ImageMaterial} from './materials';
import {EncompassPointCloud, PointCloudManager, RawPointCloud} from './pointcloud';
import {PanoControls} from './controls';
import {PanoramicList} from './panoramics';
import {CameraMarkers} from './markers';
import {sendWindowEvent, destroyWindowEvents, bindWindowEvent} from "../events";
import {AerialMap} from './aerial';
import {
  AmbientLight, Camera, DepthTexture, FloatType, Vector3,
  Mesh, MeshBasicMaterial, NearestFilter, LinearFilter,
  PerspectiveCamera, PlaneBufferGeometry, RGBAFormat,
  Scene, UnsignedIntType, WebGLRenderer, WebGLRenderTarget, TextureLoader
} from "three";
import {AssetType} from "../../redux/assetsSlice";
import {Clamp, ColorMapKey, ColorType} from '../../redux/settingsSlice';
import LocalScene from './projections';
import {nanoid} from '@reduxjs/toolkit';
import {colorMaps} from "../textures/colormaps";
import { Measurement } from './measurements/measurement';
import ChangeDetector from './utilities/change-detection';
import { isStaticSite } from '../../electron-modules';
import slash from 'slash';
import GPUPicker from './gpu-picker';

export class Viewer {
    private readonly containerElement: HTMLElement;
    public defaultFarPlane: number = 10000;
    private defaultNearPlane: number = 0.1;
    private id = nanoid();
    public camera: PerspectiveCamera;
    private light: AmbientLight;
    public renderer: WebGLRenderer;
    private screenPass: RenderPass;
    private imageMaterial: ImageMaterial;
    private texture2DDepth: WebGLRenderTarget;
    private opacityMaterial: OpacityMaterial;
    private texture2DImage: any;
    private texture2DOpacity: WebGLRenderTarget;
    public gpuPickers: {
      markers: GPUPicker,
      points: GPUPicker
    };
    private EDLMaterial: EDLMaterial;
    private canvas: any;
    private changeDetector: ChangeDetector;
    public controls: PanoControls;
    public pointclouds: PointCloudManager;
    public panoramics: PanoramicList;
    public minimap: AerialMap;
    public markers: CameraMarkers;
    public setCustomAlertHTML;
    public setCurrentCamera;
    public showMessage;
    private enabled = true;
    private focused = false;
    private drawPickerScene = false;
    public projectName;

    public timingDebug = {
      "markers": false,
      "scene": false
    };

    constructor(viewContainer, mapContainer, props) {
      this.containerElement = viewContainer;

      this.createScene();
      this.initGpuPickers();
      this.initResize();
      this.initMaterials();
      this.initViewEvents();

      this.setCustomAlertHTML = props.setCustomAlertHTML;
      this.setCurrentCamera = props.setCurrentCamera;
      this.showMessage = props.showMessage;

      this.pointclouds = new PointCloudManager(this);
      this.controls = new PanoControls(this, this.camera,
        this.renderer.domElement, props);
      this.panoramics = new PanoramicList(this);
      this.minimap = new AerialMap(this, mapContainer);
      this.markers = new CameraMarkers(this);
      this.changeDetector = new ChangeDetector(this);

      // Force initial resize event
      this.handleWindowResize();

      // Start our animation loop
      this.animate();
    }

    get width(): number {
      return this.containerElement.offsetWidth;
    }

    get height(): number {
      return this.containerElement.offsetHeight;
    }

    get near() {
      return this.camera.near;
    }

    get far() {
      return this.camera.far;
    }

    get lookat() {
      let lookat = new Vector3(0,0, -1);
      let quat = this.camera.quaternion;
      lookat.applyQuaternion(quat);
      return lookat;
    }

    get renderTargetAttrs() {
      return {
        tDepth: this.renderTarget.depthTexture,
        colorMap: this.renderTarget.texture,
      };
    }

    get renderTarget() {
      return this.EDLSupport ? this.texture2DDepth : this.texture2DOpacity;
    }

    get renderMaterial() {
      return this.EDLSupport ? this.EDLMaterial : this.opacityMaterial;
    }

    get orbitState() {
      return this.controls.orbit.state;
    }

    get cameraSelected() {
      return this.panoramics.current !== null;
    }

    get hasPoints() {
      return !this.pointclouds.isEmpty;
    }

    get hasCameras() {
      return !this.panoramics.isEmpty;
    }

    get isImageOnly() {
      return this.hasCameras && !this.hasPoints;
    }

    get EDLSupport() : boolean {
      let capabilities = this.renderer.capabilities;
      let supported = (capabilities.maxVaryings > 8)
        && capabilities.floatFragmentTextures;

      return supported;
    }

    get isWebGL2(): boolean {
      let capabilities = this.renderer.capabilities;
      return capabilities.isWebGL2;
    }

    /** Exposed Global Methods */
    setProjectName(name) {
      this.projectName = name;
    }

    togglePickerDebug(value) {
      this.drawPickerScene = value;
    }

    getAvailableClassifications(): number[] {
      return this.pointclouds.uniqueClassifications;
    }

    setVisibleClassifications(classifications) {
      classifications.forEach(classification => {
        this.pointclouds.setClassification(classification.id, classification.visible);
      });
    }

    setPointCloudOpacity(opacity: number) {
      this.pointclouds.transparency = opacity;
    }

    setPointCloudSize(size: number) {
      this.pointclouds.materialDefaults.size = size;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.size = size;
        });
      });
    }

    setPointCloudDistance(distance: number) {
      let value = (distance === 100) ? this.far : distance * 10;
      this.pointclouds.materialDefaults.distance = value;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.distance = value;
        });
      });
    }

    setPointCloudHeightClip(clamp: Clamp) {
      this.pointclouds.updateClamping('heightClamp', 'height_clamp', clamp);
    }

    setPointCloudIntensityClip(clamp: Clamp) {
      this.pointclouds.updateClamping('intensityClamp', 'intensity_clamp', clamp);
    }

    setPointCloudColorMap(colorMapKey: ColorMapKey) {
      const textureData = colorMaps.find(colorMap => colorMap.id === colorMapKey).image;

      const loader = new TextureLoader();
      loader.load(textureData, texture => {
        this.pointclouds.materialDefaults.colorMap = colorMapKey;
        this.pointclouds.clouds.forEach(pointCloud => {
          pointCloud.materials.forEach(material => {
            material.color_map = texture;
            material.color_map.needsUpdate = true;
          });
        });
      });
    }

    setPointCloudColorType(colorType: ColorType) {
      this.pointclouds.materialDefaults.colorType = colorType;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.color_type = colorType;
        });
      });
    }

    setPointCloudDynamicSize(dynamicSize: boolean) {
      this.pointclouds.materialDefaults.dynamicSize = dynamicSize;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.dynamic_size = dynamicSize;
        });
      });
    }

    setPointCloudCircularPoints(circularPoints: boolean) {
      this.pointclouds.materialDefaults.circularPoints = circularPoints;
      this.pointclouds.clouds.forEach(pointCloud => {
        pointCloud.materials.forEach(material => {
          material.circularPoints = circularPoints;
        });
      });
    }

    /** NOTE: Temporary(?) solution for the viewer being created over and over again */
    destroy() {
      destroyWindowEvents();

      // Destroy viewer elements
      this.enabled = false;
      this.camera = null;
      this.containerElement.removeChild(this.canvas);
      this.canvas = null;
      this.destroyViewEvents();

      // Destroy minimap dom element
      this.minimap.destroy();

      // TODO: destroy other things
    }

    setDataProjection(newProjection) {
      LocalScene.setDataProjection(newProjection);

      // Reset all measurements (not able to convert easily. would be easier if we changed
      // the encompass style data to be written in its original units, and converted on the fly
      if (this.controls.measurements.enabled) {
        this.controls.measurements.reset();
        this.controls.measurements.enable();
      }
    }

    setViewProjection(newProjection) {
      LocalScene.setViewProjection(newProjection);

      // Update measurement units
      const units = LocalScene.viewProjectionUnits;
      this.controls.measurements.setUnits(units);
    }

    setPanoramicVisible(cameraID) {
      this.panoramics.loadImage(cameraID);
    }

    setActiveCamera(cameraID) {
      this.minimap.setActiveCamera(cameraID);
    }

    toggleMeasurements(state) {
      if (state) {
        this.controls.measurements.enable();
      } else {
        this.controls.measurements.clear();
        this.controls.measurements.disable();
      }
    }

    toggleCameraAligner(state) {
      this.controls.cameraAlignerAdv.setState(state);
    }

    updateMeasureType(measureType) {
      this.controls.measurements.setMeasurementType(measureType);
    }

    updateMeasureHover(measurement: Measurement, state) {
      this.controls.measurements.updateRenderColor(measurement, state);
    }

    deleteMeasurement(measurement: Measurement) {
      this.controls.measurements.removeByID(measurement.id);
    }

    renameMeasurement(measurement: Measurement, title) {
      this.controls.measurements.setTitle(measurement.id, title);
    }

    exportMeasurements(type, ordering) {
      this.controls.measurements.export(type, ordering);
    }

    getMeasureExportType() {
      return this.controls.measurements.measureExportType;
    }

    loadMeasurementData(data) {
      return this.controls.measurements.loadMeasurementData(data);
    }

    /* Camera Aligner Function Calls */
    resetCameraAligner() {
      this.controls.cameraAlignerAdv.reset();
    }

    checkIfAligned() {
      return this.panoramics.aligned;
    }

    setResetCameraAligner(bool) {
      this.controls.cameraAlignerAdv.setResetAlignerManager(bool);
    }

    addRowCameraAligner() {
      this.controls.cameraAlignerAdv.addRow();
    }

    setErrorsCameraAligner(errors) {
      this.controls.cameraAlignerAdv.setErrors(errors);
    }

    getErrorCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getError(i);
    }

    deleteRowCameraAligner(i) {
      this.controls.cameraAlignerAdv.deleteRow(i);
    }

    getImageObsCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getImageObs(i);
    }

    getPointObsCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getPointObs(i);
    }

    getCamPoseCameraAligner(i) {
      return this.controls.cameraAlignerAdv.getCamPose(i);
    }

    getNumEntriesCameraAligner() {
      return this.controls.cameraAlignerAdv.numEntries;
    }

    getCamIdFromAligner(i) {
      return this.controls.cameraAlignerAdv.getCamId(i);
    }

    setBasicAlignerState(state) {
      this.controls.cameraAlignerBasic.setState(state);
    }

    resetBasicAlignerCamera(values) {
      this.controls.cameraAlignerBasic.reset(values);
    }

    getCamName(i) {
      return this.controls.cameraAlignerAdv.getCamName(i);
    }

    getCurrentCameraName() {
      return this.panoramics.current?.name;
    }

    setObservationTypeCameraAligner(type) {
      this.controls.cameraAlignerAdv.setObservationType(type);
    }

    setCurrentRowCameraAligner(rowNum) {
      this.controls.cameraAlignerAdv.setCurrentRow(rowNum);
    }
    /* End - Camera Aligner Function Calls */

    getCamerasForCSV = (assetID) => {
      const allCameras = this.panoramics.cameras;
      return allCameras.filter(x => x.csv === assetID);
    }

    getCloudPercent(identifier) {
      let cloud = this.pointclouds.clouds.find(x => x.identifier === identifier);
      if (cloud && cloud instanceof RawPointCloud) {
        return cloud.percent;
      }

      return 0.0;
    }

    getCloudMissing(identifier) {
      let cloud = this.pointclouds.clouds.find(x => x.identifier === identifier);
      if (cloud) {
        return cloud.fileMissing;
      }

      return true;
    }

    reconcilePanoramics(panoramics, cameraHeight, cameraAdjustments, currentCamera) {
      const cameraList = this.panoramics.cameras;
      const cloudList = this.pointclouds.clouds;
      const currentPanoramics = new Set(cameraList.map(camera => camera.id));
      const allCameras = panoramics.map(panoramic => {
        return panoramic.data.map(camera => {
          return {
            ...camera,
            id: `${panoramic.id}-${camera.name}`,
            csv: panoramic.id
          };
        });
      }).flat();

      const newPanoramics = new Set(allCameras.map(camera => camera.id));
      const panoramicsToLoad = allCameras.filter(camera => !currentPanoramics.has(camera.id));
      const panoramicsToDelete = cameraList.filter(camera => !newPanoramics.has(camera.id));

      if ((cameraList.length === 0) && (cloudList.length === 0)) {
        LocalScene.resetSceneShift();
      }

      panoramicsToDelete.forEach(panoramic => {
        this.panoramics.deleteCamera(panoramic.id);
      });

      panoramicsToLoad.forEach((panoramic, index) => {
        const rotation = [
          panoramic.roll,
          panoramic.pitch,
          panoramic.yaw
        ];

        const position = [
          panoramic.x,
          panoramic.y,
          panoramic.z
        ];

        const adjustment = panoramic.id in cameraAdjustments
          ? cameraAdjustments[panoramic.id]
          : [0, 0, 0];

        const cameraInfo = {
          id: panoramic.id,
          csv: panoramic.csv,
          name: panoramic.name,
          path: slash(panoramic.path),
          rotation: rotation,
          position: position,
          height: cameraHeight,
          adjustment: adjustment,
          remote: isStaticSite
        };

        this.panoramics.addNewCamera(cameraInfo, currentCamera);
      });

      const numCameras = cameraList.length;
      const numRemoved = panoramicsToDelete.length;
      const numAdded = panoramicsToLoad.length;
      const numChanged = numRemoved + numAdded;

      // Redraw cameras in scene and aerial view after changes
      if ((numAdded > 0) || (numRemoved > 0)) {
        this.minimap.addCameraMarkers(cameraList);
        this.markers.addCameraMarkers(cameraList);
        this.controls.measurements.refreshMeasurementList();
      }

      // All cameras have been removed. Force orbit mode instead
      if ((numCameras === 0) && (numRemoved > 0)) {
        this.panoramics.setOrbitCamera();
        this.setCurrentCamera(null);
        return;
      }

      // Camera changed (added or removed) and nothing visible.
      // Switch to the first camera in our new camera list instead
      if ((numChanged > 0) && (!this.panoramics.cameraLoaded)) {
        const cameraList = this.panoramics.cameras;
        if (cameraList.length > 0) {
          this.setPanoramicVisible(cameraList[0].id);
        }
      }
    }

    reconcilePointClouds(pointClouds) {
      const cameraList = this.panoramics.cameras;
      const cloudList = this.pointclouds.clouds;
      const currentPointClouds = new Set(cloudList.map(cloud => cloud.identifier));
      const newPointClouds = new Set(pointClouds.map(cloud => cloud.id));

      const pointCloudsToLoad = pointClouds.filter(cloud => !currentPointClouds.has(cloud.id));
      const pointCloudsToDelete = cloudList.filter(cloud => !newPointClouds.has(cloud.identifier));

      if ((cameraList.length === 0) && (cloudList.length === 0)) {
        LocalScene.resetSceneShift();
      }

      // Delete clouds that need to be removed
      pointCloudsToDelete.forEach(pointCloud => {
        this.pointclouds.deletePointCloud(pointCloud.identifier);
      });

      // Add new pointclouds
      pointCloudsToLoad.forEach(pointCloud => {
        const cloudDetails = {
          name: pointCloud.name,
          identifier: pointCloud.id,
          path: slash(pointCloud.data.path),
          visible: pointCloud.visible,
          remote: isStaticSite
        };

        if (pointCloud.type === AssetType.LAS) {
          let pointCloud = new RawPointCloud(this.pointclouds);
          pointCloud.loadFile(cloudDetails);
        } else if (pointCloud.type === AssetType.Encompass) {
          let pointCloud = new EncompassPointCloud(this.pointclouds);
          pointCloud.loadNodes(cloudDetails);
        }
      });

      // Toggle proper visibility for each cloud
      pointClouds.forEach(pointCloud => {
        this.pointclouds.toggleVisibility(pointCloud.id, pointCloud.visible);
      });

      const numClouds = this.pointclouds.clouds.length;
      const numCameras = this.panoramics.cameras.length;
      const numRemoved = pointCloudsToDelete.length;

      // No cloud remain. Exit 3d mode if cameras exist
      if ((numClouds === 0) && (numRemoved > 0) && (numCameras > 0)) {
        this.controls.setOrbitState(false);
      }
    }

    setCameraHeights(height) {
      this.panoramics.setCameraHeights(height);
    }

    setCameraAdjustments(adjustments) {
      this.panoramics.setCameraAdjustments(adjustments);
    }

    setCameraMatrix(matrix) {
      this.panoramics.setCameraMatrix(matrix);
      if (this.controls.cameraAlignerAdv.enabled){
        this.controls.cameraAlignerAdv.redrawImageObsMarkers();
      }
    }

    resetCameraMatrix() {
      this.panoramics.resetCameraMatrix();
      if (this.controls.cameraAlignerAdv.enabled){
        this.controls.cameraAlignerAdv.redrawImageObsMarkers();
      }
    }

    resetInitialZoom() {
      this.pointclouds.setInitialZoom(false);
    }

    resetAerialMap() {
      setTimeout(() => {
        this.minimap.reset();
      }, 50);
    }

    removeInteractions() {
      const measurements = this.controls.measurements;
      if (measurements.enabled) {
        measurements.measurementAerialView.removeInteraction();
      }
    }

    /** Private Methods */
    onKeyDown(event) {
      if (!this.focused) {
        return;
      }

      event.preventDefault();
      this.panoramics.onKeyDown(event);
      this.controls.cameraAlignerBasic.onKeyDown(event);
    }

    onMouseFocus(event) {
      if (event.type === 'mouseover') {
        this.focused = true;
      } else if (event.type === 'mouseout') {
        this.focused = false;
      }
    }

    initViewEvents() {
      this.onMouseFocus = this.onMouseFocus.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      // Keep track of our canvas focus
      this.containerElement.addEventListener( 'mouseover', this.onMouseFocus, false );
      this.containerElement.addEventListener( 'mouseout', this.onMouseFocus, false );

      // Keydown doesnt work on canvas
      window.addEventListener( 'keydown', this.onKeyDown, false) ;
    }

    destroyViewEvents() {
      this.containerElement.removeEventListener( 'mouseover', this.onMouseFocus, false );
      this.containerElement.removeEventListener( 'mouseout', this.onMouseFocus, false );
      window.removeEventListener( 'keydown', this.onKeyDown, false) ;
    }

    updatePickers() {
      Object.keys(this.gpuPickers).forEach(name => {
        this.updatePicker(name);
      });
    }

    updatePicker(name) {
      this.gpuPickers[name].needsUpdate = true;
    }

    updatePickerCamera() {
      const pickers = Object.values(this.gpuPickers);
      pickers.forEach(picker => {
        picker.setCamera(this.camera);
      });
    }

    createScene() {
      this.camera = new PerspectiveCamera(90, 1.0,
        this.defaultNearPlane, this.defaultFarPlane);

      this.renderer = new WebGLRenderer({
        antialias: false,
        alpha: true,
        logarithmicDepthBuffer: false,
        powerPreference: "high-performance"
      });

      if (this.isWebGL2) {
        console.log("WebGL2 Detected");
      }

      this.renderer.autoClear = false;
      this.canvas = this.renderer.domElement;
      this.containerElement.appendChild(this.canvas);
    }

    initGpuPickers() {
      this.gpuPickers = {
        markers: new GPUPicker(this, "markers", false),
        points: new GPUPicker(this, "points", false)
      };

      for (const picker of Object.values(this.gpuPickers)) {
        picker.setCamera(this.camera);
      }
    }

    initMaterials() {
      // Create our render pass
      this.screenPass = new RenderPass();

      // Default render target / material
      this.opacityMaterial = new OpacityMaterial();
      this.texture2DOpacity = new WebGLRenderTarget(
        this.width, this.height, {
          format: RGBAFormat,
          minFilter: NearestFilter,
          magFilter: NearestFilter,
          stencilBuffer: false
        }
      );

      // Material for rendering panoramic as background
      this.imageMaterial = new ImageMaterial();
      this.texture2DImage = new WebGLRenderTarget(
        this.width, this.height, {
          minFilter: LinearFilter,
          magFilter: LinearFilter,
          format: RGBAFormat,
          stencilBuffer: false
        }
      );

      if (!this.EDLSupport) {
        console.log("EDL Not Supported");
        return;
      }

      // EDL render target / material
      console.log("EDL Supported");
      this.EDLMaterial = new EDLMaterial(this);
      this.texture2DDepth = new WebGLRenderTarget(
        this.width, this.height, {
          format: RGBAFormat,
          minFilter: NearestFilter,
          magFilter: NearestFilter,
          generateMipmaps: false,
          stencilBuffer: false,
          depthBuffer: true
        }
      );

      let texturePrecision = this.isWebGL2 ? FloatType : UnsignedIntType;
      this.texture2DDepth.depthTexture = new DepthTexture(0, 0);
      this.texture2DDepth.depthTexture.type = texturePrecision;
    }

    initResize() {
      bindWindowEvent('resize', () => {
        this.handleWindowResize();
      });
    }

    handleWindowResize() {
      let width = this.containerElement.clientWidth;
      let height = this.containerElement.clientHeight;
      this.resizeCanvas(width, height);
      this.updatePickers();
    }

    resizeCanvas(width, height) {
      let aspect = width / height;

      this.camera.aspect = aspect;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);

      if (this.EDLSupport) {
        this.EDLMaterial.screenWidth = width;
        this.EDLMaterial.screenHeight = height;
      }
    }

    resizeRenderTargets() {
      let width = this.width;
      let height = this.height;


      // Resize render targets
      let canvas = [
        this.texture2DDepth,
        this.texture2DOpacity,
      ];

      canvas.forEach(c => {
        if (!c) {
          return;
        }

        let w = c.width;
        let h = c.height;
        if ((w !== width) || (h !== height)) {
          c.dispose();
          c.setSize(width, height);
        }
      });

      // Resize gpu picker targets
      const pickers = Object.values(this.gpuPickers);
      pickers.forEach(picker => {
        picker.resizeTarget(width, height);
      });
    }

    updateMaterial(material, values) {
      let keys = Object.keys(values);
      keys.forEach(key => {
        material[key] = values[key];
      });
    }

    setRenderTarget(target, clear = false) {
      this.renderer.setRenderTarget(target);
      if (clear) {
        this.renderer.clear();
      }
    }

    renderPanoramic() {
      // Render panoramic image
      this.setRenderTarget(this.texture2DImage, true);
      this.renderer.render(this.panoramics.scene, this.camera);

      // Update material texture with results
      this.updateMaterial(this.imageMaterial, {
        colorMap: this.texture2DImage.texture
      });

      // Render to screen and clear depth
      this.screenPass.render(this.renderer, this.imageMaterial);
      this.renderer.clearDepth();
    }

    renderSceneObjects() {
      let transparentMarkers = this.orbitState;

      // All items renderered here will be full opacity regardless
      this.setRenderTarget(this.renderTarget, true);
      if (!transparentMarkers) {
        this.renderer.render(this.markers.scene, this.camera);
      }
      this.updateMaterial(this.renderMaterial, {
        ...this.renderTargetAttrs, opacity: 1.0
      });
      this.screenPass.render(this.renderer, this.renderMaterial);

      // Objects rendered here will have transparency applied
      this.setRenderTarget(this.renderTarget);
      this.renderer.render(this.pointclouds.scene, this.camera);
      this.updateMaterial(this.renderMaterial, {
        ...this.renderTargetAttrs, opacity: this.pointclouds.transparency
      });
      this.screenPass.render(this.renderer, this.renderMaterial);

      // Semi-transparent markers sometimes rendered on top of everything
      if (transparentMarkers) {
        this.setRenderTarget(this.renderTarget, true);
        this.renderer.render(this.markers.scene, this.camera);
        this.updateMaterial(this.renderMaterial, {
          ...this.renderTargetAttrs, opacity: this.markers.transparency
        });
        this.screenPass.render(this.renderer, this.renderMaterial);
      }

      // Items here are rendered on top of everything, so they can always be seen
      this.setRenderTarget(this.renderTarget, true);
      this.renderer.render(this.controls.measureScene, this.camera);
      this.renderer.render(this.controls.alignerScene, this.camera);
      this.updateMaterial(this.renderMaterial, {
        ...this.renderTargetAttrs, opacity: 1.0
      });
      this.screenPass.render(this.renderer, this.renderMaterial);
    }

    renderPickerScene() {
      this.gpuPickers.points.needsUpdate = true;
      this.gpuPickers.points.update();
      this.setRenderTarget(null, true);
      this.renderer.render(this.gpuPickers.points.scene, this.camera);
    }

    updateSceneElements() {
      this.changeDetector.update();
      this.pointclouds.update();
      this.controls.update(this.changeDetector);
      this.panoramics.update();
      this.minimap.update(this.changeDetector);
      this.markers.update();
    }

    animate() {
      if (!this.enabled) {
        return;
      }

      // Trigger stats start
      sendWindowEvent("stats.begin");

      this.resizeRenderTargets();
      this.updateSceneElements();

      if (this.drawPickerScene) {
        this.renderPickerScene();
      } else {
        this.renderPanoramic();
        this.renderSceneObjects();
      }

      // Trigger stats finished
      let pointcloud = this.pointclouds;
      sendWindowEvent("stats.end", {
        number_nodes: pointcloud.numSceneNodesVisible,
        visible_points: pointcloud.numVisiblePoints,
        max_points: pointcloud.visiblePointsTarget
      });

      requestAnimationFrame(() => {
        this.animate();
      });
    }
}

class RenderPass {
    private readonly screenScene: Scene;
    private readonly screenQuad: Mesh;
    private readonly camera: Camera;

    constructor() {
      const geometry = new PlaneBufferGeometry(2, 2, 0);
      const material = new MeshBasicMaterial({
        depthTest: true,
        depthWrite: true,
        transparent: true
      });

      this.screenScene = new Scene();
      this.screenQuad = new Mesh(geometry, material);
      this.screenScene.add(this.screenQuad);
      this.camera = new Camera();
    }

    /** Render to screen. Note that our render target is changed after using this function */
    render(renderer, material, target = null) {
      this.screenQuad.material = material;
      renderer.setRenderTarget(target);
      renderer.render(this.screenScene, this.camera);
    }
}
